import { useEffect, useState } from "react"
import apiClient from "../../Utils/apiClient"
import { Badge, Button, Card, Col, Row, Tag, Drawer, DatePicker } from "antd"
import PanelLayout from "../../Layout/PanelLayout"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import {
  CalendarOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import { useHeaderContext } from "../../Utils/HeaderContext"
import AutoCompleteBox from "../../components/AutoCompleteBox/AutoCompleteBox"

dayjs.extend(relativeTime)

const getExactTimeDiff = (date) => {
  const now = dayjs()
  const input = dayjs(date)
  const diffSeconds = now.diff(input, "second")
  const diffMinutes = now.diff(input, "minute")
  const diffHours = now.diff(input, "hour")
  const diffDays = now.diff(input, "day")

  if (diffSeconds < 60) {
    return `${diffSeconds} second${diffSeconds !== 1 ? "s" : ""} ago`
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`
  } else {
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`
  }
}

const ViewLogs = () => {
  const { render, setRender } = useHeaderContext()
  const [logs, setLogs] = useState([])
  const [groupedLogs, setGroupedLogs] = useState({})
  const [hostName, setHostName] = useState("")
  const [type, setType] = useState("")
  const [expandedIndexes, setExpandedIndexes] = useState({})

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
  })

  useEffect(() => {
    const localUrl = window.location.href
    const name = localUrl.split("/").at(-2)
    const tab = localUrl.split("/").at(-1)

    setHostName(name)
    setType(tab)
  }, [])

  useEffect(() => {
    if (hostName && type) {
      getData()
    }
  }, [hostName, type, render])

  useEffect(() => {
    groupLogsByDate(logs)
  }, [logs])

  const handleOpenLog = async (log) => {
    await apiClient("logs/mark-seen", "post", { logId: log._id })
    setRender((prev) => !prev)
  }

  const getData = async (currentFilters = filters) => {
    try {
      let entityTypes = []
      switch (type) {
        case "Patches":
          entityTypes = ["upgradewindows"]
          break
        case "winget-updates":
          entityTypes = ["upgradewinget"]
          break
        case "choco-update":
          entityTypes = ["upgradechoco"]
          break
        case "Softwares":
          entityTypes = ["upgradewinget", "upgradechoco"]
          break
        default:
          entityTypes = []
      }

      const filterPayload = { hostName, entityTypes }

      if (currentFilters.createdFor) {
        filterPayload.createdFor = currentFilters.createdFor
      }
      if (currentFilters.fromDate) {
        filterPayload.fromDate = currentFilters.fromDate
          .startOf("day")
          .toISOString()
      }
      if (currentFilters.toDate) {
        filterPayload.toDate = currentFilters.toDate.endOf("day").toISOString()
      }

      const response = await apiClient(
        "logs/getByEntityTypes",
        "post",
        filterPayload
      )
      if (response.result) {
        const sortedLogs = response.result.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )
        setLogs(sortedLogs)
      }
    } catch (err) {
      console.error("error on View Log page :", err)
    }
  }

  const groupLogsByDate = (allLogs) => {
    const grouped = {}
    allLogs.forEach((log) => {
      const date = dayjs(log.createdAt).format("YYYY-MM-DD")
      if (!grouped[date]) {
        grouped[date] = []
      }
      grouped[date].push(log)
    })

    const sortedGrouped = Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((obj, key) => {
        obj[key] = grouped[key]
        return obj
      }, {})

    setGroupedLogs(sortedGrouped)
  }

  const toggleExpand = (logId) => {
    setExpandedIndexes((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }))
  }

  const handleFilter = async (filter) => {
    const data = await apiClient("filter/logs", "POST", {
      createdFor: hostName,
      ...filter,
    })
    setLogs(data?.result || [])
  }

  const handleClearFilter = () => {
    const clearedFilters = {
      fromDate: null,
      toDate: null,
    }
    setFilters(clearedFilters)
    getData(clearedFilters)
    setDrawerOpen(false)
  }

  return (
    <PanelLayout
      title={`Logs for ${hostName}`}
      subtitle="Filtered activity history for this device tab."
      actions={
        <FilterOutlined onClick={() => setDrawerOpen(true)} style={{ fontSize: 16, cursor: "pointer" }} />
      }
    >
        <Drawer
          title={
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Apply Filter</span>
              <CloseOutlined
                onClick={() => setDrawerOpen(false)}
                className="rounded-circle p-1"
                style={{
                  color: "rgba(0,0,0,0.6)",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              />
            </div>
          }
          mask={false}
          placement="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          width={300}
        >
          <div className="d-flex flex-column justify-content-between h-100">
            <div className="p-3">
              <div className="mb-3">
                <label className="form-label">From Date</label>
                <DatePicker
                  className="w-100"
                  value={filters.fromDate}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, fromDate: value }))
                  }
                />
              </div>

              <div className="mb-3">
                <label className="form-label">To Date</label>
                <DatePicker
                  className="w-100"
                  value={filters.toDate}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, toDate: value }))
                  }
                />
              </div>
            </div>

            <div className="p-3 d-flex gap-2">
              <Button
                size="small"
                className="btn-form-filter rounded-pill"
                onClick={() => handleFilter(filters)}
              >
                Apply Filter
              </Button>
              <Button
                size="small"
                className="btn-form-filter rounded-pill"
                onClick={handleClearFilter}
              >
                Clear Filter
              </Button>
            </div>
          </div>
        </Drawer>
      <div className="logs">
          {Object.keys(groupedLogs).length > 0 ? (
            Object.keys(groupedLogs).map((date) => (
              <div key={date} style={{ marginBottom: "2rem" }}>
                <p className="mb-3">
                  <CalendarOutlined style={{ marginRight: 8 }} />
                  {dayjs(date).format("DD MMMM YYYY")}
                  <Tag color="blue" style={{ marginLeft: 10 }}>
                    {groupedLogs[date].length} Logs
                  </Tag>
                </p>
                {groupedLogs[date].map((log) => {
                  const isExpanded = expandedIndexes[log._id]

                  return (
                    <Badge.Ribbon
                      key={log._id}
                      text={!log.isOpened ? "New" : log.status}
                      className="text-capitalize"
                      color={
                        !log.isOpened
                          ? "red"
                          : log.status === "pending"
                          ? "orange"
                          : log.status === "working"
                          ? "blue"
                          : log.status === "success"
                          ? "green"
                          : "cyan"
                      }
                    >
                      <Card
                        style={{ marginBottom: "1rem" }}
                        onClick={() => handleOpenLog(log)}
                      >
                        <Row gutter={[12, 2]}>
                          <Col sm={24} md={24} lg={12}>
                            <h3 className="fw-bold font-18 text-capitalize">
                              {`${log.createdFor} Log`}
                            </h3>

                            <ul
                              style={{
                                listStyleType: "none",
                                paddingLeft: "0",
                              }}
                            >
                              <li>
                                <strong>Created At: </strong>

                                {dayjs(log.createdAt).format("hh:mm A")}
                                <span style={{ color: "#888", marginLeft: 8 }}>
                                  {getExactTimeDiff(log.createdAt)}
                                </span>
                              </li>
                            </ul>

                            <Button
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleExpand(log._id)
                              }}
                              type="default"
                              size="small"
                              className="mt-2 font-14"
                            >
                              {isExpanded ? "Show Less" : "Show More ..."}
                            </Button>
                          </Col>

                          <Col sm={24} md={24} lg={12}>
                            <ul className="mb-0 ps-3 text-capitalize">
                              <li>
                                <strong>User :</strong> {log.createdFor}
                              </li>
                              <li>
                                <strong>Command :</strong> {log.title}
                              </li>
                              <li>
                                <strong>Status :</strong>{" "}
                                <Tag
                                  className="text-capitalize"
                                  color={
                                    log.status === "pending"
                                      ? "orange"
                                      : log.status === "working"
                                      ? "blue"
                                      : log.status === "success"
                                      ? "green"
                                      : "cyan"
                                  }
                                >
                                  {log.status}
                                </Tag>
                              </li>
                            </ul>
                          </Col>
                        </Row>

                        {isExpanded && (
                          <>
                            {log.data &&
                              log.data.map((item, i) => (
                                <div key={i} className="mb-2">
                                  <Card>
                                    <ul className="mb-0 text-capitalize">
                                      {Object.entries(item).map(
                                        ([key, value], idx) => (
                                          <li key={idx}>
                                            <strong>
                                              {key.replace(/([A-Z])/g, " $1")}:{" "}
                                            </strong>
                                            {String(value)}
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </Card>
                                </div>
                              ))}

                            {log.output && (
                              <p>
                                {/* <strong>Output :</strong> {log.output} */}
                                {log.output !== "pending"
                                  ? log.output
                                  : log.status}
                              </p>
                            )}
                          </>
                        )}
                      </Card>
                    </Badge.Ribbon>
                  )
                })}
              </div>
            ))
          ) : (
            <p>No logs found for the selected criteria.</p>
          )}
      </div>
    </PanelLayout>
  )
}

export default ViewLogs
